import { useState } from "react";
import { Mail, Phone, MapPin, MessageSquare, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { usePublicSettings } from "@/hooks/usePublicSiteData";
import { useSiteLanguage } from "@/contexts/LanguageContext";
import { getPublicUiCopy } from "@/lib/publicPageLocalization";

export default function Contact() {
  const { language } = useSiteLanguage();
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    country: "",
    message: "",
  });

  const { data: settings } = usePublicSettings();
  const ui = getPublicUiCopy(language, "contact");

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        ...form,
        inquiry_type: "general",
      });
      setDone(true);
      toast.success(ui.inquirySentToast);
    } catch (err) {
      toast.error(formatApiError(err) || ui.inquiryErrorToast);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-[var(--rsi-bg-subtle)]" data-testid="contact-page">
      <div className="container-rsi grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <span className="badge-purple">
            <MessageSquare className="h-3.5 w-3.5" /> {settings?.contact_badge || "Trade Desk"}
          </span>
          <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter mt-3 leading-[1.02] text-[var(--rsi-navy)]">
            {settings?.contact_title || "Let's source"}<br />{settings?.contact_highlight || "something great."}
          </h1>
          <p className="mt-5 text-gray-700 max-w-md">
            {settings?.contact_subtitle ||
              "Whether you need a quote, a supplier audit, or end-to-end logistics — message us and a real human will reply within 24 business hours."}
          </p>

          <div className="mt-10 space-y-5 text-sm">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-[var(--rsi-purple-tint)] grid place-items-center text-[var(--rsi-purple)]">
                <MapPin className="h-4 w-4" />
              </div>
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">{ui.address}</div>
                <div className="text-[var(--rsi-navy)] font-medium mt-0.5">
                  {settings?.address || "Natun Bazar, Barishal Sadar, Barishal, Bangladesh"}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-[var(--rsi-purple-tint)] grid place-items-center text-[var(--rsi-purple)]">
                <Phone className="h-4 w-4" />
              </div>
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">{ui.phone}</div>
                <div className="text-[var(--rsi-navy)] font-medium mt-0.5">
                  <a href={`tel:${settings?.phone_primary}`} className="hover:text-[var(--rsi-purple)]">
                    {settings?.phone_primary || "+8801722-772003"}
                  </a>
                  {" · "}
                  <a href={`tel:${settings?.phone_secondary}`} className="hover:text-[var(--rsi-purple)]">
                    {settings?.phone_secondary || "+8613036168820"}
                  </a>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-[var(--rsi-purple-tint)] grid place-items-center text-[var(--rsi-purple)]">
                <Mail className="h-4 w-4" />
              </div>
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">{ui.email}</div>
                <a
                  href={`mailto:${settings?.email || "info@rsintl23.com"}`}
                  className="text-[var(--rsi-navy)] font-medium mt-0.5 hover:text-[var(--rsi-purple)]"
                >
                  {settings?.email || "info@rsintl23.com"}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="rsi-card p-8">
            {!done ? (
              <form onSubmit={submit} className="grid gap-4">
                <h2 className="font-display font-extrabold text-2xl tracking-tight text-[var(--rsi-navy)] mb-2">
                  {settings?.contact_form_title || "Send an inquiry"}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="c-company">{ui.companyName}</Label>
                    <Input
                      id="c-company"
                      required
                      value={form.company_name}
                      onChange={handleChange("company_name")}
                      data-testid="contact-company"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="c-contact">{ui.yourName}</Label>
                    <Input
                      id="c-contact"
                      required
                      value={form.contact_name}
                      onChange={handleChange("contact_name")}
                      data-testid="contact-name"
                      className="mt-1.5"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="c-email">{ui.businessEmail}</Label>
                    <Input
                      id="c-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange("email")}
                      data-testid="contact-email"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="c-phone">{ui.phoneOptional}</Label>
                    <Input
                      id="c-phone"
                      value={form.phone}
                      onChange={handleChange("phone")}
                      data-testid="contact-phone"
                      className="mt-1.5"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="c-country">{ui.country}</Label>
                  <Input
                    id="c-country"
                    value={form.country}
                    onChange={handleChange("country")}
                    data-testid="contact-country"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="c-message">{ui.message}</Label>
                  <Textarea
                    id="c-message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={handleChange("message")}
                    data-testid="contact-message"
                    className="mt-1.5"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> {ui.sending}
                    </>
                  ) : (
                    <>
                      {ui.sendInquiry} <Send className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            ) : (
              <div className="text-center py-10" data-testid="contact-success">
                <div className="mx-auto h-16 w-16 rounded-full bg-[var(--rsi-green)]/10 grid place-items-center mb-4">
                  <CheckCircle2 className="h-8 w-8 text-[var(--rsi-green)]" />
                </div>
                <h3 className="font-display text-2xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
                  {settings?.contact_success_title || "Message Sent"}
                </h3>
                <p className="text-gray-600 mt-2 max-w-md mx-auto">
                  {settings?.contact_success_message || "Our trade desk will respond within 24 business hours."}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
